/**
 * PreviewWarRoom — pixel-faithful render of the ClaimSync "War Room" template
 * (Daylight variant supplied), with a light/dark toggle.
 *
 * The case header (case no, location, title, exposure fact) is wired to a REAL
 * claim (most-recent from /api/v1/reports). The agent deliberation content —
 * agent positions, contested figures, consensus output, and the run log — is
 * largely qualitative debate-transcript material, so it uses the template's
 * representative static content (like PreviewClientPortal). This is a design
 * preview, not the live deliberation surface.
 *
 * Mounted at /preview/war-room. Self-contained — does NOT touch the real
 * /deliberation route, feature flags, or any working surface.
 *
 * Black + orange discipline. Green ("done/current/supported") on the completed
 * run-log nodes + the consensus complete chip only; contested/attention states
 * stay ORANGE, never red.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import {
  SERIF, BODY, MONO, PREVIEW_ZOOM, Palette, ChipKind,
  useTheme, ThemeToggle, chipStyle, fmtUSD, relTime,
} from './preview/previewTheme';

interface ApiReport {
  id: string;
  client_name?: string;
  status?: string;
  result?: { final_claim_amount?: number };
  created_at?: string;
  updated_at?: string;
}

// ---- left rail nav (representative) ----
interface NavItem { id: string; label: string; icon: string; badge?: string; active?: boolean }
interface NavGroup { group: string; items: NavItem[] }
const NAV_GROUPS: NavGroup[] = [
  { group: 'Work Desk', items: [{ id: 'desk', label: 'Work Desk', icon: '◧' }] },
  { group: 'Cases', items: [{ id: 'cases', label: 'All Claims', icon: '▤' }, { id: 'new', label: 'New Claim', icon: '＋' }] },
  { group: 'Review', items: [{ id: 'engine', label: 'Engine Review', icon: '◈' }, { id: 'anom', label: 'Anomalies', icon: '◬', badge: '7' }, { id: 'attest', label: 'Attestations', icon: '✺', badge: '2' }] },
  { group: 'Deliverables', items: [{ id: 'gen', label: 'Generators', icon: '❏' }, { id: 'export', label: 'Export Center', icon: '⤓' }] },
  { group: 'Intelligence', items: [{ id: 'warroom', label: 'War Room', icon: '✺', active: true }, { id: 'copilot', label: 'Co-pilot', icon: '✦' }, { id: 'audit', label: 'Engine Audit', icon: '⊞' }] },
  { group: 'Admin', items: [{ id: 'activity', label: 'Activity Log', icon: '≣' }] },
];

// ---- agent deliberation content (representative / template) ----
type AgentStatus = 'computing' | 'waiting';
interface AgentRow { label: string; value: string; dim?: boolean }
interface AgentCard {
  name: string; role: string; mark: string; status: string; statusKind: AgentStatus;
  rows: AgentRow[]; conf: string; confW: string; dim?: boolean;
}
const AGENTS: AgentCard[] = [
  {
    name: 'Forensic agent', role: 'Financial reconstruction', mark: 'FA',
    status: 'Computing', statusKind: 'computing',
    rows: [
      { label: 'But-for revenue', value: '$1,005,000' },
      { label: 'Avoided var. costs', value: '$293,632' },
      { label: 'Continuing expenses', value: '$148,200' },
      { label: 'Gross BI loss', value: '$312,000' },
    ],
    conf: '91%', confW: '91%',
  },
  {
    name: 'Adjuster agent', role: 'Policy interpretation', mark: 'AA',
    status: 'Computing', statusKind: 'computing',
    rows: [
      { label: 'Coverage confirmed', value: 'Yes' },
      { label: 'Waiting period', value: '72 hrs' },
      { label: 'Coinsurance', value: 'Met' },
      { label: 'Recommended payout', value: '$284,400' },
    ],
    conf: '87%', confW: '87%',
  },
  {
    name: 'Attorney agent', role: 'Legal exposure', mark: 'LE',
    status: 'Waiting on forensic', statusKind: 'waiting', dim: true,
    rows: [
      { label: '§542 interest risk', value: '—', dim: true },
      { label: 'Bad-faith exposure', value: '—', dim: true },
      { label: 'Dispute likelihood', value: '—', dim: true },
      { label: 'Settlement range', value: '—', dim: true },
    ],
    conf: '—', confW: '0%',
  },
];

interface ConsensusCell { label: string; value: string; note: string; orange?: boolean; ghost?: boolean }
const CONSENSUS: ConsensusCell[] = [
  { label: 'Gross BI loss', value: '$312,000', note: 'Forensic calc' },
  { label: 'Recommended payout', value: '$284,400', note: 'Post-policy adj.' },
  { label: 'Avg. confidence', value: '89%', note: '2-agent avg', orange: true },
  { label: 'Legal exposure', value: '—', note: 'Attorney pending', ghost: true },
];

type LogKind = 'done' | 'running' | 'queued';
interface LogEntry { kind: LogKind; line: boolean; title: string; detail: string; time: string }
const LOG: LogEntry[] = [
  { kind: 'done', line: true, title: 'Pipeline initialized', detail: 'BI-5512 · Riverside Kitchen & Tap loaded', time: '09:41:02' },
  { kind: 'done', line: true, title: 'RAG retrieval complete', detail: '14 policy docs, 6 financials indexed', time: '09:41:18' },
  { kind: 'running', line: true, title: 'Forensic agent running', detail: 'bottom-up calculator active', time: '09:41:33' },
  { kind: 'running', line: true, title: 'Adjuster agent running', detail: 'policy terms parsed', time: '09:41:41' },
  { kind: 'queued', line: false, title: 'Attorney agent', detail: 'queued', time: '—' },
];

export default function PreviewWarRoom() {
  const { theme, P, toggle } = useTheme();
  const navigate = useNavigate();
  const [report, setReport] = useState<ApiReport | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api.get('/api/v1/reports?limit=200', { showErrorToast: false });
        const list: ApiReport[] = Array.isArray(data?.reports) ? data.reports : Array.isArray(data) ? data : [];
        const sorted = [...list].sort((a, b) =>
          new Date(b.updated_at || b.created_at || 0).getTime() - new Date(a.updated_at || a.created_at || 0).getTime());
        if (alive) setReport(sorted[0] || null);
      } catch {
        if (alive) setReport(null);
      }
    })();
    return () => { alive = false; };
  }, []);

  const clientName = report?.client_name || 'Riverside Kitchen & Tap';
  const caseNo = report ? `BI-${(report.id || '').slice(0, 8).toUpperCase()} · AUSTIN, TX` : 'HFS-TX-2023-88214 · AUSTIN, TX';
  const exposure = report?.result?.final_claim_amount;

  const FACTS = useMemo(() => ([
    { label: 'Loss date', value: 'Sep 14, 2023' },
    { label: 'Restoration', value: 'Feb 14, 2024' },
    { label: 'Policy limit', value: '$1,000,000' },
    { label: 'Indemnity', value: exposure ? fmtUSD(exposure) : '12 mo.' },
  ]), [exposure]);

  const goReport = () => { if (report?.id) navigate(`/report/${report.id}`); };

  // shared inline helpers
  const orangeDot = (pulse: boolean): React.CSSProperties => ({
    width: 7, height: 7, borderRadius: '50%', background: P.orange, display: 'inline-block',
    ...(pulse ? { animation: 'pulse 1.4s ease-in-out infinite' } : {}),
  });
  const ghostDot7: React.CSSProperties = { width: 7, height: 7, borderRadius: '50%', background: P.textGhost, display: 'inline-block' };

  const logDot = (kind: LogKind): React.CSSProperties => {
    const base: React.CSSProperties = { width: 10, height: 10, borderRadius: '50%', flex: '0 0 10px', marginTop: 3 };
    if (kind === 'done') return { ...base, background: P.greenSolid };
    if (kind === 'running') return { ...base, background: P.orange, animation: 'pulse 1.4s ease-in-out infinite' };
    return { ...base, background: 'transparent', border: `1.5px solid ${P.textGhost}` };
  };

  return (
    <div style={{ zoom: PREVIEW_ZOOM, height: '100vh', width: '100%', overflow: 'hidden', background: P.pageBg, fontFamily: BODY, color: P.textBody, display: 'flex', flexDirection: 'column' }}>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.35}}`}</style>

      {/* This is a design mock with representative STATIC content — its figures never
          update. Users kept landing here expecting the live deliberation and reading
          stale numbers as real. The banner is deliberately unmissable. */}
      <div style={{ background: P.orange, color: '#1a1a1a', padding: '10px 20px', fontSize: 13.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 10, flex: '0 0 auto' }}>
        <span style={{ textTransform: 'uppercase', letterSpacing: '1px', border: '1.5px solid #1a1a1a', borderRadius: 4, padding: '2px 8px', fontSize: 10.5 }}>Static design preview</span>
        <span>
          These numbers are NOT live. Open your claim's real War Room at{' '}
          <a href="/dashboard" style={{ color: '#1a1a1a', textDecoration: 'underline', fontWeight: 700 }}>
            Dashboard → claim → War Room
          </a>{' '}
          (route: /deliberation/&#123;claimId&#125;).
        </span>
      </div>

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

      {/* ============ LEFT RAIL ============ */}
      <aside style={{ width: 240, flex: '0 0 240px', background: P.pageBg, display: 'flex', flexDirection: 'column', height: '100%', borderRight: `1px solid ${P.railHairline}` }}>
        <div style={{ padding: '20px 20px 16px', display: 'flex', alignItems: 'center', gap: 11 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: P.orange, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 30px' }}>
            <div style={{ width: 12, height: 12, border: '2.5px solid #fefefe', borderRadius: '50%' }} />
          </div>
          <div style={{ lineHeight: 1 }}>
            <div style={{ fontFamily: SERIF, color: P.textPrimary, fontSize: 20, letterSpacing: '0.2px' }}>The Figure Co.</div>
            <div style={{ fontSize: 9.5, letterSpacing: '1.4px', color: P.textMuted, textTransform: 'uppercase', marginTop: 2 }}>Forensic BI Platform</div>
          </div>
        </div>
        <nav style={{ flex: 1, overflowY: 'auto', padding: '6px 0 16px' }}>
          {NAV_GROUPS.map((grp) => (
            <div key={grp.group} style={{ margin: '10px 0 2px' }}>
              <div style={{ fontSize: 10, letterSpacing: '1.3px', color: P.textMuted, textTransform: 'uppercase', fontWeight: 600, padding: '6px 22px 4px' }}>{grp.group}</div>
              {grp.items.map((item) => (
                <a
                  key={item.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 11, padding: '8px 18px 8px 19px', fontSize: 13.5, fontWeight: 500,
                    cursor: 'pointer', textDecoration: 'none',
                    borderLeft: `3px solid ${item.active ? P.orange : 'transparent'}`,
                    color: item.active ? P.textPrimary : P.textLabel,
                    background: item.active ? P.elevBg : 'transparent',
                  }}
                >
                  <span style={{ fontSize: 14, width: 18, textAlign: 'center', opacity: 0.9 }}>{item.icon}</span>
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {item.badge && (
                    <span style={{ fontSize: 10.5, fontWeight: 600, background: P.orange, color: P.onOrange, borderRadius: 999, padding: '1px 7px', minWidth: 18, textAlign: 'center' }}>{item.badge}</span>
                  )}
                </a>
              ))}
            </div>
          ))}
        </nav>
        <div style={{ padding: '12px 16px', borderTop: `1px solid ${P.railHairline}`, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: P.elevBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: P.textBody, fontWeight: 600, fontSize: 12.5, flex: '0 0 32px' }}>TM</div>
          <div style={{ flex: 1, lineHeight: 1.25, minWidth: 0 }}>
            <div style={{ color: P.textPrimary, fontSize: 13, fontWeight: 500 }}>Tyler Mercer</div>
            <div style={{ color: P.textLabel, fontSize: 11, display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 5, height: 5, borderRadius: '50%', background: P.orange, display: 'inline-block' }} />Internal Accountant</div>
          </div>
          <span style={{ color: P.textMuted, fontSize: 15 }}>⌄</span>
        </div>
      </aside>

      {/* ============ MAIN ============ */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>

        {/* TOPBAR */}
        <header style={{ height: 58, flex: '0 0 58px', background: P.pageBg, borderBottom: `1px solid ${P.railHairline}`, display: 'flex', alignItems: 'center', padding: '0 22px', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: P.textLabel }}>
            <span style={{ cursor: 'pointer' }}>Claims</span><span style={{ color: P.accentNeutral }}>›</span>
            <span style={{ color: P.textBody, cursor: 'pointer' }} onClick={goReport}>{clientName}</span><span style={{ color: P.accentNeutral }}>›</span>
            <span style={{ color: P.textPrimary, fontWeight: 500 }}>War Room</span>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 9, background: P.elevBg, border: `1px solid ${P.cardBorder}`, borderRadius: 9, padding: '7px 12px', width: 280, cursor: 'text', color: P.textMuted, fontSize: 13 }}>
            <span style={{ fontSize: 13 }}>⌕</span><span style={{ flex: 1 }}>Search…</span>
            <span style={{ fontFamily: MONO, fontSize: 11, background: P.pageBg, border: `1px solid ${P.controlBorder}`, borderRadius: 5, padding: '1px 6px', color: P.textLabel }}>⌘K</span>
          </div>
          <button style={{ width: 36, height: 36, borderRadius: 9, border: `1px solid ${P.cardBorder}`, background: P.elevBg, color: P.textBody, fontSize: 15, cursor: 'pointer' }}>◷</button>
          <ThemeToggle P={P} theme={theme} onToggle={toggle} />
        </header>

        {/* CONTENT */}
        <main style={{ flex: 1, overflow: 'auto', background: P.pageBg, display: 'flex', flexDirection: 'column' }}>
          <div style={{ flex: 1, padding: '28px 32px 24px', maxWidth: 1280, margin: '0 auto', width: '100%' }}>

            {/* HEADER */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, marginBottom: 22 }}>
              <div>
                <div style={{ fontSize: 12, letterSpacing: '1.4px', color: P.textMuted, textTransform: 'uppercase', fontWeight: 600, marginBottom: 7 }}>{caseNo}</div>
                <h1 style={{ fontFamily: SERIF, fontSize: 32, color: P.textPrimary, margin: 0, fontWeight: 400 }}>Business interruption — fire loss</h1>
                <div style={{ display: 'flex', gap: 30, marginTop: 16, flexWrap: 'wrap' }}>
                  {FACTS.map((f) => (
                    <div key={f.label}>
                      <div style={{ fontSize: 11.5, color: P.textMuted }}>{f.label}</div>
                      <div style={{ fontSize: 14, color: P.textPrimary, fontWeight: 500, marginTop: 2 }}>{f.value}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, border: `1px solid ${P.orangeTintBd}`, borderRadius: 999, padding: '9px 16px', flex: '0 0 auto' }}>
                <span style={orangeDot(true)} />
                <span style={{ fontSize: 13.5, color: P.orangeText, fontWeight: 600 }}>Agents running</span>
              </div>
            </div>

            {/* AGENT CARDS */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 18 }}>
              {AGENTS.map((a) => (
                <div key={a.name} style={{ background: P.cardBg, border: `1px solid ${a.dim ? P.divider : P.cardBorder}`, borderRadius: 14, padding: 22 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                    <div style={{ opacity: a.dim ? 0.5 : 1 }}>
                      <div style={{ fontSize: 16, color: P.textPrimary, fontWeight: 600 }}>{a.name}</div>
                      <div style={{ fontSize: 12.5, color: P.textLabel, marginTop: 2 }}>{a.role}</div>
                    </div>
                    <div style={{ width: 34, height: 34, borderRadius: 8, background: P.elevBg, border: `1px solid ${P.cardBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: MONO, fontSize: 11, color: P.textMuted, flex: '0 0 34px', opacity: a.dim ? 0.5 : 1 }}>{a.mark}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 14 }}>
                    <span style={a.statusKind === 'computing' ? orangeDot(true) : ghostDot7} />
                    <span style={{ fontSize: 13, fontWeight: 500, color: a.statusKind === 'computing' ? P.orangeText : P.textLabel }}>{a.status}</span>
                  </div>
                  <div style={{ height: 1, background: P.divider, margin: '16px 0' }} />
                  {a.rows.map((r) => (
                    <div key={r.label} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, padding: '6px 0' }}>
                      <span style={{ fontSize: 13, color: P.textLabel, opacity: r.dim ? 0.5 : 1 }}>{r.label}</span>
                      <span style={{ fontFamily: MONO, fontSize: 14, fontWeight: 500, color: r.dim ? P.textGhost : P.textPrimary }}>{r.value}</span>
                    </div>
                  ))}
                  <div style={{ marginTop: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
                      <span style={{ fontSize: 12.5, color: P.textLabel, opacity: a.dim ? 0.5 : 1 }}>Confidence</span>
                      <span style={{ fontFamily: MONO, fontSize: 13, fontWeight: 500, color: a.dim ? P.textGhost : P.orangeText }}>{a.conf}</span>
                    </div>
                    <div style={{ height: 4, background: P.divider, borderRadius: 999, overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: 999, width: a.confW, background: a.dim ? P.textGhost : P.orange }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* CONSENSUS */}
            <div style={{ background: P.cardBg, border: `1px solid ${P.cardBorder}`, borderRadius: 14, padding: 24, marginBottom: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                <h2 style={{ fontFamily: SERIF, fontSize: 20, color: P.textPrimary, margin: 0, fontWeight: 400 }}>Consensus output</h2>
                <span style={chipStyle('neutral' as ChipKind, P)}>2 of 3 agents complete</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16 }}>
                {CONSENSUS.map((c) => (
                  <div key={c.label} style={{ background: P.elevBg, border: `1px solid ${P.cardBorder}`, borderRadius: 12, padding: 18 }}>
                    <div style={{ fontSize: 11, letterSpacing: '0.6px', textTransform: 'uppercase', color: P.textLabel, fontWeight: 600 }}>{c.label}</div>
                    <div style={{ fontFamily: MONO, fontSize: 30, fontWeight: 500, marginTop: 10, color: c.ghost ? P.textGhost : c.orange ? P.orangeText : P.textPrimary }}>{c.value}</div>
                    <div style={{ fontSize: 12, color: P.textMuted, marginTop: 6 }}>{c.note}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* RUN LOG */}
            <div style={{ background: P.cardBg, border: `1px solid ${P.cardBorder}`, borderRadius: 14, padding: 24 }}>
              <h2 style={{ fontFamily: SERIF, fontSize: 20, color: P.textPrimary, margin: '0 0 18px', fontWeight: 400 }}>Run log</h2>
              {LOG.map((l, i) => (
                <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', paddingBottom: 18, position: 'relative' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 12px' }}>
                    <span style={logDot(l.kind)} />
                    {l.line && <span style={{ width: 1, flex: 1, background: P.divider, marginTop: 4, minHeight: 20 }} />}
                  </div>
                  <div style={{ flex: 1, marginTop: -2 }}>
                    <div style={{ fontSize: 13.5, color: l.kind === 'running' ? P.orangeText : P.textLabel }}>
                      <span style={{ fontWeight: 600 }}>{l.title}</span> · {l.detail}
                    </div>
                    <div style={{ fontSize: 11.5, color: P.textMuted, fontFamily: MONO, marginTop: 3 }}>{l.time === '—' ? '—' : `${l.time} · ${relTime(report?.updated_at)}`}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* BOTTOM ACTION BAR */}
          <div style={{ position: 'sticky', bottom: 0, background: P.elevBg, borderTop: `1px solid ${P.railHairline}`, padding: '14px 32px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <button style={{ display: 'flex', alignItems: 'center', gap: 9, background: P.elevBg, border: `1px solid ${P.controlBorder}`, borderRadius: 9, padding: '10px 18px', fontSize: 14, fontWeight: 600, color: P.textPrimary, cursor: 'pointer', fontFamily: BODY }}>
              <span style={{ width: 14, height: 14, border: `1.5px solid ${P.textMuted}`, borderRadius: 3, display: 'inline-block' }} /> Resume pipeline
            </button>
            <button style={{ background: P.elevBg, border: `1px solid ${P.controlBorder}`, borderRadius: 9, padding: '10px 18px', fontSize: 14, fontWeight: 500, color: P.textBody, cursor: 'pointer', fontFamily: BODY }}>Export report</button>
            <button style={{ background: P.elevBg, border: `1px solid ${P.controlBorder}`, borderRadius: 9, padding: '10px 18px', fontSize: 14, fontWeight: 500, color: P.textBody, cursor: 'pointer', fontFamily: BODY }}>Override values</button>
            <button style={{ marginLeft: 'auto', background: 'transparent', border: `1px solid ${P.controlBorder}`, borderRadius: 9, padding: '10px 20px', fontSize: 14, fontWeight: 500, color: P.textLabel, cursor: 'pointer', fontFamily: BODY }}>Abort run</button>
          </div>
        </main>
      </div>
      </div>
    </div>
  );
}
